import React from 'react';
import { Outlet, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { PreLoginHeader } from 'components/Header/PreLoginHeader';
import { Masthead } from 'ui';
import { PreLoginFooter } from 'components/Footer/PreLoginFooter';
import { useSelector } from 'react-redux';
import { getIsLoggedIn } from 'store/auth/selectors';
import { BroadcastBannerStack } from '../BroadcastBannerStack';
import { useFeatureFlags } from 'feature-flags/useFeatureFlags';
import { LoginPage } from 'pages/LandingPage/LoginPage';
import { lazyWithRetry } from '../../utils/lazyWithRetry';
import { PageContentWrapper } from '../PageContentWrapper';
import { FormBuilderRouter } from './FormBuilderRouter';

const NotFoundPage = lazyWithRetry(() => import('pages/Errors/NotFoundPage'));
const LandingPage = lazyWithRetry(() => import('pages/LandingPage/LandingPage'));
const TermsOfUsePage = lazyWithRetry(() => import('pages/InfoPages/TermsOfUsePage/TermsOfUsePage'));
const PrivacyStatementPage = lazyWithRetry(() => import('pages/InfoPages/PrivacyStatementPage/PrivacyStatementPage'));
const ContactUsPage = lazyWithRetry(() => import('pages/InfoPages/ContactUsPage/ContactUsPage'));

const FeatureFlagTogglePage = lazyWithRetry(() => import('pages/DevTools/FeatureFlagTogglePage'));
const ManifestDocumentationPage = lazyWithRetry(() => import('pages/DevTools/ManifestDocumentationPage'));

const ProjectTeamBuilderPage = lazyWithRetry(
  () => import('pages/DevTools/ProjectTeamBuilderPage/ProjectTeamBuilderPage'),
);

const PublicRoutesWrapper: React.FC = () => {
  const { pathname } = useLocation();

  return (
    <PageContentWrapper>
      <div className="flex flex-col">
        <div className="flex flex-col w-full min-h-screen max-w-screen">
          <Masthead />
          <div className="sticky top-0 z-40">
            <PreLoginHeader showLoginButton={pathname !== '/login-with-singpass'} />
          </div>
          <BroadcastBannerStack />
          <div className="flex flex-grow">
            <Outlet />
          </div>
        </div>
        <PreLoginFooter />
      </div>
    </PageContentWrapper>
  );
};

export const MainPublicRouter: React.FC = () => {
  const isLoggedIn = useSelector(getIsLoggedIn);
  const { showFormBuilder, enableToggleFeatureFlagPanel, enableSubmissionHelpers, enableProjectTeamBuilder } =
    useFeatureFlags();

  return (
    <Routes>
      <Route element={<PublicRoutesWrapper />}>
        <Route path="/" element={isLoggedIn ? <Navigate to="auth/projects" replace /> : <LandingPage />} />
        <Route path="/login-with-singpass" element={<LoginPage />} />
        <Route path="/terms-of-use" element={<TermsOfUsePage />} />
        <Route path="/privacy-statement" element={<PrivacyStatementPage />} />
        <Route path="/contact-us" element={<ContactUsPage />} />
        {enableToggleFeatureFlagPanel && <Route path="/toggle-flags" element={<FeatureFlagTogglePage />} />}
        {enableSubmissionHelpers && <Route path="/manifest-doc" element={<ManifestDocumentationPage />} />}
        {enableProjectTeamBuilder && <Route path="/project-team-builder" element={<ProjectTeamBuilderPage />} />}
        <Route path="/*" element={<NotFoundPage isLoggedIn={false} />} />
      </Route>
      {showFormBuilder && <Route path="/form-builder/*" element={<FormBuilderRouter />} />}
    </Routes>
  );
};

export default MainPublicRouter;
